import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useCreateTask, type CreateTaskInput } from "@/api/tasks";
import { useGetDispatchCenters } from "@/api/dispatch-centers";
import { useShipmentById, useShipments } from "@/api/shipments";

// Form validation schema with Zod
const formSchema = z.object({
  type: z.enum([
    "CUSTOMER_PICKUP",
    "CUSTOMER_DELIVERY",
    "CARRIER_DROPOFF",
    "CUSTOMER_RETURN",
    "DEPOT_RECEIVE",
    "DEPOT_SORT",
    "DEPOT_DISPATCH",
    "DEPOT_TRANSFER",
  ]),
  status: z
    .enum(["PENDING", "IN_PROGRESS", "COMPLETED", "FAILED", "CANCELLED"])
    .optional(),
  shipmentId: z.string().min(1, "Shipment is required"),
  dispatchCenterId: z.string().min(1, "Dispatch center is required"),
  waypointId: z.string().optional(),
  tripId: z.string().optional(),
  routingAddressId: z.string().optional(),
  description: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface CreateTaskDialogProps {
  onCreated?: () => void;
  initialShipmentId?: string | null;
  initialWaypointId?: string | null;
  initialTripId?: string | null;
}

export function CreateTaskDialog({
  onCreated,
  initialShipmentId,
  initialWaypointId,
  initialTripId,
}: CreateTaskDialogProps) {
  const [open, setOpen] = useState(false);

  // Fetch dispatch centers for dropdown
  const { data: dispatchCenters } = useGetDispatchCenters();

  // Fetch shipments for dropdown
  const { data: shipmentsResponse } = useShipments({ limit: 50 });

  // Setup form with react-hook-form and zod validation
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type: "CUSTOMER_PICKUP",
      status: "PENDING",
      shipmentId: initialShipmentId ?? "",
      dispatchCenterId: "",
      waypointId: initialWaypointId ?? "",
      tripId: initialTripId ?? "",
      routingAddressId: "",
      description: "",
    },
  });

  const selectedShipmentId = form.watch("shipmentId");
  const { data: selectedShipment } = useShipmentById(
    selectedShipmentId || null
  );
  const routingAddresses = selectedShipment?.addresses?.filter(
    (entry) => entry.address?.location
  );

  // Effect to update form when initialShipmentId changes
  useEffect(() => {
    if (initialShipmentId) {
      form.setValue("shipmentId", initialShipmentId);
    }
  }, [initialShipmentId, form]);

  // Handle dialog closing
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      form.reset({
        type: "CUSTOMER_PICKUP",
        status: "PENDING",
        shipmentId: "",
        dispatchCenterId: "",
        waypointId: "",
        tripId: "",
        routingAddressId: "",
        description: "",
      });
    }
  };

  // Create task mutation
  const { mutate: createTask, isPending } = useCreateTask({
    onSuccess: () => {
      toast.success("Task created successfully");
      handleOpenChange(false);
      onCreated?.();
    },
    onError: (error) => {
      toast.error("Failed to create task", { description: error.message });
    },
  });

  // Handle form submission
  const onSubmit = (values: FormValues) => {
    const routingAddress = selectedShipment?.addresses?.find(
      (entry) => entry.id === values.routingAddressId
    )?.address;
    if (values.routingAddressId && !routingAddress?.location) {
      toast.error("Choose a valid routing address");
      return;
    }

    const taskInput: CreateTaskInput = {
      type: values.type,
      status: values.status,
      shipmentId: values.shipmentId,
      dispatchCenterId: values.dispatchCenterId,
      tripId: values.tripId || undefined,
      description: values.description,
      address: routingAddress?.location
        ? {
            line1: routingAddress.line1,
            line2: routingAddress.line2 || undefined,
            city: routingAddress.city,
            province: routingAddress.province,
            country: routingAddress.country,
            postalCode: routingAddress.postalCode || undefined,
            location: routingAddress.location,
          }
        : undefined,
    };

    createTask(taskInput);
  };

  const content = (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* Task Type Selection */}
        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Task Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a task type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="CUSTOMER_PICKUP">
                    Customer Pickup
                  </SelectItem>
                  <SelectItem value="CUSTOMER_DELIVERY">
                    Customer Delivery
                  </SelectItem>
                  <SelectItem value="CARRIER_DROPOFF">
                    Carrier Dropoff
                  </SelectItem>
                  <SelectItem value="CUSTOMER_RETURN">
                    Customer Return
                  </SelectItem>
                  <SelectItem value="DEPOT_RECEIVE">
                    Dispatch Center Receive
                  </SelectItem>
                  <SelectItem value="DEPOT_SORT">
                    Dispatch Center Sort
                  </SelectItem>
                  <SelectItem value="DEPOT_DISPATCH">
                    Dispatch Center Dispatch
                  </SelectItem>
                  <SelectItem value="DEPOT_TRANSFER">
                    Dispatch Center Transfer
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Status Selection */}
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Initial Status</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Shipment Selection */}
        <FormField
          control={form.control}
          name="shipmentId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Shipment</FormLabel>
              <Select
                onValueChange={(shipmentId) => {
                  field.onChange(shipmentId);
                  form.setValue("routingAddressId", "");
                }}
                defaultValue={field.value}
                disabled={!!initialShipmentId}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a shipment" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {shipmentsResponse?.data.map((shipment) => (
                    <SelectItem key={shipment.id} value={shipment.id}>
                      {shipment.trackingNumber ||
                        `SHP-${shipment.shipmentNumber}`}{" "}
                      ({shipment.status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {routingAddresses && routingAddresses.length > 0 && (
          <FormField
            control={form.control}
            name="routingAddressId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Routing address (Optional)</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a shipment address" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {routingAddresses.map((entry) => (
                      <SelectItem key={entry.id} value={entry.id}>
                        {entry.type} · {entry.address?.line1},{" "}
                        {entry.address?.city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Dispatch Center Selection */}
        <FormField
          control={form.control}
          name="dispatchCenterId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Dispatch Center</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a dispatch center" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {dispatchCenters?.map((center) => (
                    <SelectItem key={center.id} value={center.id}>
                      {center.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Description Input */}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description (Optional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter task description"
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (initialShipmentId) {
                onCreated?.();
              } else {
                handleOpenChange(false);
              }
            }}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? "Creating..." : "Create Task"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );

  // If this is being used as an embedded form (with initialShipmentId)
  if (initialShipmentId) {
    return content;
  }

  // Otherwise, render as a dialog
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!initialShipmentId && (
        <DialogTrigger asChild>
          <Button className="gap-2">
            <Plus className="h-4 w-4" />
            Create Task
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Task</DialogTitle>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
